<template>
  <div class="area-unidades-table">
    <div class="subtitle-2 mb-1">{{ $t('area.units.title') }}</div>

    <v-alert v-if="unidadesFora.length" type="error" dense outlined class="mb-2">
      {{ $t('area.units.outsideError', { units: nomes(unidadesFora) }) }}
    </v-alert>
    <v-alert v-if="unidadesSemCentro.length" type="warning" dense outlined class="mb-2">
      {{ $t('area.units.noCenterWarning', { units: nomes(unidadesSemCentro) }) }}
    </v-alert>
    <v-alert v-if="foraEscopo > 0" type="info" dense outlined class="mb-2">
      {{ $t('area.units.outOfScope', { count: foraEscopo }) }}
    </v-alert>

    <v-row v-if="canEdit" dense align="center">
      <v-col cols="10">
        <MultiplaEscolha
          ref="associarSelect"
          lazy
          get-url="getUnidadeslazy"
          text="descricao_unidade"
          value="id_unidade"
          :item-label="rotuloUnidade"
          :label="$t('area.units.associate')"
          :max-items="3"
          v-bind:selected-items.sync="paraAssociar"
        />
      </v-col>
      <v-col cols="2" class="text-right">
        <v-btn
          color="primary"
          dark
          small
          fab
          :disabled="!paraAssociar.length"
          :title="$t('area.units.associate')"
          @click="associar"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

    <v-list dense class="py-0 area-unidades-table__list">
      <v-list-item v-for="u in unidades" :key="'au-' + u.id_unidade">
        <v-list-item-icon class="mr-2">
          <v-icon small :color="corSituacao(u.situacao_posicao)">{{ iconeSituacao(u.situacao_posicao) }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ rotuloUnidade(u) }}</v-list-item-title>
          <v-list-item-subtitle :class="classeSituacao(u.situacao_posicao)">
            {{ textoSituacao(u.situacao_posicao) }}
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="canEdit">
          <v-btn icon small :title="$t('area.units.remove')" @click="$emit('remove', u.id_unidade)">
            <v-icon small color="red">mdi-link-variant-off</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
      <v-list-item v-if="!loading && !unidades.length">
        <v-list-item-content>
          <v-list-item-title class="text--secondary text-body-2">{{ $t('area.units.empty') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <template v-if="canEdit && sugestoes.length">
      <div class="d-flex align-center mt-3 mb-1">
        <span class="subtitle-2">{{ $t('area.units.suggestedTitle', { count: sugestoes.length }) }}</span>
        <v-spacer />
        <v-btn x-small text color="primary" @click="$emit('add', sugestoes.map((u) => u.id_unidade))">
          <v-icon x-small left>mdi-playlist-plus</v-icon>{{ $t('area.units.addAllSuggested') }}
        </v-btn>
      </div>
      <div class="caption grey--text mb-1">{{ $t('area.units.suggestedHint') }}</div>
      <v-chip-group column>
        <v-chip
          v-for="u in sugestoes"
          :key="'sug-' + u.id_unidade"
          small
          outlined
          color="success"
          @click="$emit('add', [u.id_unidade])"
        >
          <v-icon x-small left>mdi-plus</v-icon>{{ rotuloUnidade(u) }}
        </v-chip>
      </v-chip-group>
    </template>
  </div>
</template>

<script>
import MultiplaEscolha from "@/components/Selects/MultiplaEscolha.vue";
import { SITUACAO } from "@/utils/areasGeometria";
import { corSituacaoUnidade } from "@/utils/areasMapa";

/**
 * Unidades de uma Área de Planejamento: situação de cada uma em relação ao polígono
 * (dentro; fora = erro que impede salvar; sem posição = aviso), sugestões pelo polígono e
 * associação manual. O estado fica no CRUD (salvo junto com a área).
 */
export default {
  name: "AreaUnidadesTable",
  components: { MultiplaEscolha },
  props: {
    unidades: { type: Array, default: () => [] },
    sugestoes: { type: Array, default: () => [] },
    foraEscopo: { type: Number, default: 0 },
    canEdit: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data: () => ({
    paraAssociar: [],
  }),
  computed: {
    unidadesFora() {
      return this.unidades.filter((u) => u.situacao_posicao === SITUACAO.FORA);
    },
    unidadesSemCentro() {
      return this.unidades.filter((u) => u.situacao_posicao === SITUACAO.SEM_CENTRO);
    },
  },
  methods: {
    rotuloUnidade(u) {
      return [u.cod_unidade, u.descricao_unidade].filter(Boolean).join(" - ") || String(u.id_unidade);
    },
    nomes(lista) {
      return lista.map((u) => u.cod_unidade || u.descricao_unidade || u.id_unidade).join(", ");
    },
    corSituacao(s) {
      return corSituacaoUnidade(s);
    },
    iconeSituacao(s) {
      if (s === SITUACAO.DENTRO) return "mdi-map-marker-check";
      if (s === SITUACAO.FORA) return "mdi-map-marker-alert";
      return "mdi-map-marker-off";
    },
    textoSituacao(s) {
      if (s === SITUACAO.DENTRO) return this.$t("area.units.situation.inside");
      if (s === SITUACAO.FORA) return this.$t("area.units.situation.outside");
      if (s === SITUACAO.SEM_CENTRO) return this.$t("area.units.situation.noCenter");
      return this.$t("area.units.situation.pending");
    },
    classeSituacao(s) {
      if (s === SITUACAO.FORA) return "red--text";
      if (s === SITUACAO.SEM_CENTRO) return "orange--text text--darken-2";
      return "";
    },
    associar() {
      const ids = (this.paraAssociar || []).map(Number).filter((n) => Number.isFinite(n) && n > 0);
      this.paraAssociar = [];
      const sel = this.$refs.associarSelect;
      if (sel && typeof sel.resetData === "function") sel.resetData();
      if (ids.length) this.$emit("add", ids);
    },
  },
};
</script>

<style scoped>
.area-unidades-table__list {
  max-height: 320px;
  overflow-y: auto;
}
</style>
